import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { GALAXY_UPLOADER_SERVICE_TOKEN, GalaxyBaseUploaderServiceInterface } from '@vendasta/galaxy/uploader';
import {
  InputWithDynamicContentsComponent,
  BASE64_IMAGE_VALIDATOR,
} from '../input-with-dynamic-contents/input-with-dynamic-contents.component';
import { noBase64Images } from '../../../shared/public_api';
import { ColorsSettingsComponent } from '../settings/colors-settings/colors-settings.component';
import { SizingAndSpacingSettingsComponent } from '../settings/sizing-and-spacing-settings/sizing-and-spacing-settings.component';
import { TopLogoSettingsComponent } from '../settings/top-logo-settings/top-logo-settings.component';
import { FooterSettingsComponent } from '../settings/footer-settings/footer-settings.component';
import { BlockListComponent } from '../block-list/block-list.component';
import {
  ContentData,
  ContentBlockItem,
  CONTENT_BUILDER_DEPENDENCIES,
  ColorSettings,
  FooterSettings,
  PageSettings,
  TopLogoSettings,
} from '../interface';
import { v4 as uuidv4 } from 'uuid';
import { TranslateModule } from '@ngx-translate/core';

type ValidityKey = 'templateName' | 'subjectLine' | 'colorSettings' | 'topLogo' | 'blocks' | 'previewText';

@Component({
  selector: 'email-builder-editor',
  templateUrl: './builder.component.html',
  styleUrls: ['./builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: GALAXY_UPLOADER_SERVICE_TOKEN,
      useFactory: (): GalaxyBaseUploaderServiceInterface => inject(CONTENT_BUILDER_DEPENDENCIES).uploaderService,
    },
  ],
  imports: [
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    MatInputModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    InputWithDynamicContentsComponent,
    ColorsSettingsComponent,
    SizingAndSpacingSettingsComponent,
    TopLogoSettingsComponent,
    FooterSettingsComponent,
    BlockListComponent,
    TranslateModule,
  ],
})
export class BuilderComponent {
  private readonly aiIconService = inject(GalaxyAiIconService);
  private readonly contentBuilderDependencies = inject(CONTENT_BUILDER_DEPENDENCIES);

  readonly initialContentData = input.required<ContentData>();
  private readonly currentContentData = signal<ContentData>(null);
  readonly contentDataChange = output<ContentData>();
  readonly isValidChange = output<boolean>();

  protected readonly templateNameControl = new FormControl('', [Validators.required, noBase64Images()]);
  protected readonly subjectLineControl = new FormControl(' ', [Validators.required]);
  protected readonly inboxPreviewControl = new FormControl(' ');
  protected readonly base64Validators = [BASE64_IMAGE_VALIDATOR];

  protected readonly validityMap = signal<Partial<Record<ValidityKey, boolean>>>({});
  private readonly isValid = computed(() => Object.values(this.validityMap()).every(Boolean));

  protected readonly subjectLineElementId = `subject-line-input-${uuidv4()}`;
  protected readonly inboxPreviewElementId = `inbox-preview-input-${uuidv4()}`;

  protected readonly hasColorsSetting = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('colors') ?? false,
  );
  protected readonly hasSizingSetting = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('sizing') ?? false,
  );
  protected readonly hasTopLogoSetting = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('topLogo') ?? false,
  );
  protected readonly hasFooterSetting = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('footer') ?? false,
  );
  protected readonly hasAnySettings = computed(
    () => (this.contentBuilderDependencies.supportedSettings?.length ?? 0) > 0,
  );
  protected readonly hasSupportedBlocks = computed(
    () => (this.contentBuilderDependencies.supportedBlockTypes?.length ?? 0) > 0,
  );
  protected readonly hasTemplateNameField = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('templateName') ?? false,
  );
  protected readonly hasSubjectLineField = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('subjectLine') ?? false,
  );
  protected readonly hasPreviewTextField = computed(
    () => this.contentBuilderDependencies.supportedSettings?.includes('previewText') ?? false,
  );

  constructor() {
    this.aiIconService.dummyInit();

    // Sync input to form controls
    effect(() => {
      const data = this.initialContentData();
      if (!data) return;
      this.currentContentData.set(data);
      this.subjectLineControl.setValue(data.subjectLine || '', { emitEvent: false });
      this.inboxPreviewControl.setValue(data.previewText || '', { emitEvent: false });
      this.templateNameControl.setValue(data.name || '', { emitEvent: false });
      if (this.hasTemplateNameField()) {
        this.updateValidity('templateName', this.templateNameControl.valid);
      }
      if (this.hasPreviewTextField()) {
        this.updateValidity('previewText', this.inboxPreviewControl.valid);
      }
    });

    this.templateNameControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (this.hasTemplateNameField()) {
        this.updateValidity('templateName', this.templateNameControl.valid);
      }
      this.currentContentData.update((data) => ({
        ...data,
        name: value,
      }));
    });

    effect(() => {
      const currentData = this.currentContentData();
      if (!currentData) return;
      this.contentDataChange.emit(currentData);
    });

    effect(() => {
      this.isValidChange.emit(this.isValid());
    });
  }

  protected onSubjectLineChange(content: string): void {
    this.subjectLineControl.setValue(content);
    this.currentContentData.update((data) => ({
      ...data,
      subjectLine: content,
    }));
  }

  protected onColorSettingsChange(settings: ColorSettings): void {
    this.currentContentData.update((data) => ({
      ...data,
      contentSetup: {
        ...data.contentSetup,
        colors: settings,
      },
    }));
  }

  protected onPageSetupChange(settings: PageSettings): void {
    this.currentContentData.update((data) => ({
      ...data,
      contentSetup: {
        ...data.contentSetup,
        pageSetup: settings,
      },
    }));
  }

  protected onFooterSettingsChange(settings: FooterSettings): void {
    this.currentContentData.update((data) => ({
      ...data,
      contentSetup: {
        ...data.contentSetup,
        footer: settings,
      },
    }));
  }

  protected onTopLogoSettingsChange(settings: TopLogoSettings): void {
    this.currentContentData.update((data) => ({
      ...data,
      contentSetup: {
        ...data.contentSetup,
        topLogo: settings,
      },
    }));
  }

  protected onInboxPreviewChange(content: string): void {
    this.inboxPreviewControl.setValue(content);
    if (this.hasPreviewTextField()) {
      this.updateValidity('previewText', this.inboxPreviewControl.valid);
    }
    this.currentContentData.update((data) => ({
      ...data,
      previewText: content,
    }));
  }

  protected onContentBlocksChange(blocks: ContentBlockItem[]): void {
    this.updateValidity(
      'blocks',
      blocks.every((b) => b.valid !== false),
    );
    this.currentContentData.update((content) => {
      return {
        ...content,
        contentBlocks: blocks,
      };
    });
  }

  protected updateValidity(key: ValidityKey, valid: boolean): void {
    this.validityMap.update((map) => (map[key] === valid ? map : { ...map, [key]: valid }));
  }
}
